import { debounce } from 'lodash';
import queryString from 'query-string';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import awsConfig from "../aws-exports";
import { AuthContext } from '../contexts/AuthContext';
import { Box, Dropbox, Google, Microsoft } from '../helpers/constants';
import { createIntegrationFunc, createOauth2CredsFunc, getIntegrationsFunc, runFileSyncFunc, updateIntegrationFunc } from '../lib/helper';
import { DropboxFolderDrive, IntegrationSelectionOptions, IntegrationState, integration, integrationStatus } from '../types/integrations';

const features = {
  popup: "yes",
  width: 600,
  height: 700,
  top: "auto",
  left: "auto",
  toolbar: "no",
  menubar: "no",
};

const strWindowsFeatures = Object.entries(features)
  .reduce((str, [key, value]) => {
    if (value === "auto") {
      if (key === "top") {
        const v = Math.round(
          window.innerHeight / 2 - features.height / 2
        );
        str += `top=${v},`;
      } else if (key === "left") {
        const v = Math.round(
          window.innerWidth / 2 - features.width / 2
        );
        str += `left=${v},`;
      }
      return str;
    }

    str += `${key}=${value},`;
    return str;
  }, "")
  .slice(0, -1); // remove last ',' (comma)

const useIntegration = () => {
  const { search } = useLocation();
  const { userGroup } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [integrations, setIntegrations] = useState<IntegrationState[]>([]);
  const [integrationSelectionOptions, setIntegrationSelectionOptions] = useState<IntegrationSelectionOptions | undefined>();
  const [integrationSelectionModalOpen, setIntegrationSelectionModalOpen] = useState<boolean>(false);
  const [integrationSelectionModalLoading, setIntegrationSelectionModalLoading] = useState<boolean>(false);
  const [integrationInModal, setIntegrationInModal] = useState<integration | undefined>();
  const oauthState = useRef<string>('');

  const parsedQueryString = queryString.parse(search);

  useEffect(() => {
    if (userGroup) {
      setLoading(true);
      getIntegrationsFunc(userGroup).then(
        (data: any[]) => {
          setIntegrations(data);

          setLoading(false);
        },
        () => {
          setLoading(false);
        });
    }
  }, [userGroup]);

  useEffect(() => {
    if (parsedQueryString?.code) {
      const code = parsedQueryString?.code as string;
      const state = parsedQueryString?.state as string;
      window.opener.postMessage(JSON.stringify({ code, state }));
      window.close();
    } else if (parsedQueryString?.lc) {
      window.close();
    }
  }, [parsedQueryString.code, parsedQueryString.state, parsedQueryString.lc]);

  const receiveMessage = async (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      console.warn(`Message received by ${event.origin}; IGNORED.`);
      return;
    }

    window.removeEventListener("message", receiveMessage);

    if (event.data) {
      let code, state;
      try {
        const parsed = JSON.parse(event.data);
        code = parsed.code;
        state = parsed.state;
      } catch (e) {
        /* Ignore event.data if it is not valid json string */
        return;
      }

      if (code && state && state !== decodeURIComponent(oauthState.current)) {
        console.warn('Possible cross-site forgery attempt. IGNORED.');
        return;
      }

      const { integration: integrationToConnect } = JSON.parse(decodeURIComponent(state));

      debounceInitiateIntegrationConnection(code, integrationToConnect);
    }
  };

  const initiateIntegrationConnection = async (code: string, integrationToConnect: integration) => {
    if (integrationSelectionModalOpen) {
      return;
    }

    setIntegrationInModal(integrationToConnect);
    setIntegrationSelectionModalOpen(true);
    setIntegrationSelectionModalLoading(true);

    let result: any;
    try {
      const params = Object.assign(
        { group: userGroup, code, integration: integrationToConnect },
        window.location.href.includes('localhost') && { redirectUri: window.location.href }
      );
      result = await createOauth2CredsFunc(params);
    } catch (err) {
      setIntegrationSelectionModalLoading(false);
      setIntegrationSelectionModalOpen(false);

      console.error('There was an error connecting the integration', { err, integrationToConnect });
      return;
    }

    setIntegrationSelectionModalLoading(false);
    setIntegrationSelectionOptions(result);
  }

  // eslint-disable-next-line
  const debounceInitiateIntegrationConnection = useCallback(
    debounce(initiateIntegrationConnection, 1000, { maxWait: 1500 }),
    // function required as dependency
    [initiateIntegrationConnection]
  );

  const getAuthorizeUrl = ({ integrationToConnect }: { integrationToConnect: integration }) => {
    oauthState.current = encodeURIComponent(JSON.stringify({
      id: uuidv4(),
      integration: integrationToConnect,
    }));

    const redirectUri = (awsConfig.app_domain ? `https://${awsConfig.app_domain}` :
      'http://localhost:3000') + '/integrations';

    // @ts-ignore
    if (integrationToConnect === 'Zapier') {
      return awsConfig.environment === 'app' ? 'https://zapier.com/developer/public-invite/193840/536f2027c563e0cdd950797432468df3/' :
        'https://zapier.com/developer/public-invite/191305/efe9f454a86b98f257bb0db81b871628/';
    }

    return {
      [integration.BOX]: `https://account.box.com/api/oauth2/authorize?response_type=code&client_id=${Box.clientId}&redirect_uri=${redirectUri}&state=${oauthState.current}`,
      [integration.DROPBOX]: `https://www.dropbox.com/oauth2/authorize?client_id=${Dropbox.clientId}&redirect_uri=${redirectUri}&response_type=code&token_access_type=offline&state=${oauthState.current}`,
      [integration.MICROSOFT]: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${Microsoft.clientId}&scope=${Microsoft.scope}&response_type=code&redirect_uri=${redirectUri}&state=${oauthState.current}`,
      [integration.GOOGLE]: `https://accounts.google.com/o/oauth2/auth?client_id=${Google.clientId}&redirect_uri=${redirectUri}&scope=${Google.scope}&response_type=code&access_type=offline&prompt=consent&state=${oauthState.current}`,

    }[integrationToConnect];
  };

  const authorizeIntegration = async ({ integrationToConnect }: { integrationToConnect: integration }) => {
    window.addEventListener("message", receiveMessage, false);
    window.open(
      getAuthorizeUrl({ integrationToConnect }),
      '_blank',
      strWindowsFeatures
    );
  };

  const disconnectIntegration = async ({ integrationToDisconnect }: { integrationToDisconnect: integration }) => {
    const existingIntegration = integrations.find(x => x.integration === integrationToDisconnect);

    if (existingIntegration) {
      await updateIntegrationFunc({
        ...existingIntegration,
        status: integrationStatus.DISCONNECTED,
      });

      setIntegrations(integrations.map(x => {
        if (x.integration === integrationToDisconnect) {
          return {
            ...x,
            status: integrationStatus.DISCONNECTED,
          };
        }

        return x;
      }));
    }
  };

  const disconnectOneDrive = async () => {
    const existingIntegration = integrations.find(x => x.integration === integration.MICROSOFT);

    if (existingIntegration) {
      await disconnectIntegration({ integrationToDisconnect: integration.MICROSOFT });

      //logout
      const redirectUri = (awsConfig.app_domain ? `https://${awsConfig.app_domain}` :
        'http://localhost:3000') + '/integrations';
      window.open(
        `https://login.live.com/oauth20_logout.srf?client_id=${Microsoft.clientId}&redirect_uri=${redirectUri}`,
        '_blank',
        strWindowsFeatures
      );
    }
  }

  const saveIntegrationConnection = async (value: DropboxFolderDrive, integrationToConnect: integration) => {
    setIntegrationSelectionModalOpen(false);

    const existingIntegration = integrations.find(x => x.integration === integrationToConnect);
    const params = {
      group: userGroup,
      integration: integrationToConnect,
      externalConfig: JSON.stringify(value),
      status: integrationStatus.CONNECTED,
    };

    if (existingIntegration) {
      await updateIntegrationFunc(params);

      setIntegrations(integrations.map(x => {
        if (x.integration === integrationToConnect) {
          x = { ...x, ...params };
        }
        return x;
      }));
    } else {
      await createIntegrationFunc(params);

      setIntegrations([...integrations, params])
    }

    setIntegrationInModal(undefined);
    // intentional fire-and-forget. Syncs are a background process and we do not want to block the UI waiting for one to finish
    runFileSyncFunc(userGroup);
  }

  return {
    loading,
    integrations,
    integrationInModal,
    integrationSelectionOptions,
    integrationSelectionModalOpen,
    integrationSelectionModalLoading,
    oauthState: oauthState.current,
    setIntegrationSelectionModalLoading,
    setIntegrationSelectionModalOpen,
    setIntegrationInModal,
    getAuthorizeUrl,
    authorizeIntegration,
    disconnectIntegration,
    disconnectOneDrive,
    saveIntegrationConnection,
    initiateIntegrationConnection,
  };
};

export default useIntegration;
